import { PLAYER_RADIUS, Tile, type TileId, type Vec, inBounds, idx } from "./types";

/**
 * A world-space velocity expressed in tiles/second.
 *
 * The negative `vx`/`vy` fields deliberately make simulation bodies
 * structurally incompatible with this input. A body has both position
 * (`x`/`y`) and velocity (`vx`/`vy`), which previously allowed the whole body
 * to be passed here and interpreted its position as motion.
 */
export type VelocityInput = Vec & {
  readonly vx?: never;
  readonly vy?: never;
};

const SOLID: Partial<Record<TileId, boolean>> = {
  [Tile.Water]: true,
  [Tile.Tree]: true,
  [Tile.Fence]: true,
  [Tile.Wall]: true,
  [Tile.Stone]: true,
  [Tile.Roof]: true,
};

export function isSolid(tile: TileId): boolean {
  return !!SOLID[tile];
}

export function tileAt(tiles: Uint8Array, w: number, h: number, x: number, y: number): TileId {
  if (!inBounds(x, y, w, h)) return Tile.Wall;
  return tiles[idx(x, y, w)] as TileId;
}

export function circleBlocked(
  tiles: Uint8Array,
  w: number,
  h: number,
  x: number,
  y: number,
  r = PLAYER_RADIUS,
): boolean {
  const x0 = Math.floor(x - r);
  const y0 = Math.floor(y - r);
  const x1 = Math.floor(x + r);
  const y1 = Math.floor(y + r);
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      if (!isSolid(tileAt(tiles, w, h, tx, ty))) continue;
      const cx = Math.max(tx, Math.min(x, tx + 1));
      const cy = Math.max(ty, Math.min(y, ty + 1));
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy < r * r) return true;
    }
  }
  return false;
}

/** Integrate velocity with wall-slide. Never embeds the circle in solids. */
export function moveCircle(
  tiles: Uint8Array,
  w: number,
  h: number,
  pos: Vec,
  vel: VelocityInput,
  dt: number,
  r = PLAYER_RADIUS,
): { x: number; y: number; vx: number; vy: number; hit: boolean } {
  let x = pos.x;
  let y = pos.y;
  let vx = vel.x;
  let vy = vel.y;
  let hit = false;

  const nx = x + vx * dt;
  if (!circleBlocked(tiles, w, h, nx, y, r)) {
    x = nx;
  } else {
    vx = 0;
    hit = true;
    const step = Math.sign(nx - x) * 0.02;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const p = t + step;
      if (circleBlocked(tiles, w, h, p, y, r)) break;
      t = p;
    }
    x = t;
  }

  const ny = y + vy * dt;
  if (!circleBlocked(tiles, w, h, x, ny, r)) {
    y = ny;
  } else {
    vy = 0;
    hit = true;
    const step = Math.sign(ny - y) * 0.02;
    let t = y;
    for (let i = 0; i < 8; i++) {
      const p = t + step;
      if (circleBlocked(tiles, w, h, x, p, r)) break;
      t = p;
    }
    y = t;
  }

  x = Math.max(r + 0.05, Math.min(w - r - 0.05, x));
  y = Math.max(r + 0.05, Math.min(h - r - 0.05, y));
  return { x, y, vx, vy, hit };
}

export function los(
  tiles: Uint8Array,
  w: number,
  h: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): boolean {
  const dx = bx - ax;
  const dy = by - ay;
  const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy) * 4));
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const x = ax + dx * t;
    const y = ay + dy * t;
    if (isSolid(tileAt(tiles, w, h, Math.floor(x), Math.floor(y)))) return false;
  }
  return true;
}

export function len(v: Vec): number {
  return Math.hypot(v.x, v.y);
}

export function norm(x: number, y: number): Vec {
  const l = Math.hypot(x, y);
  if (l < 1e-6) return { x: 0, y: 0 };
  return { x: x / l, y: y / l };
}
